/**
 * Roster (T6-B2): a partner or manager adds members inline from the firm's
 * accepted staff (picker + role + Add) and removes them with a reason. The
 * client never chooses people (ADR-0047 R1).
 */
import { useState, type FormEvent } from 'react';
import { Alert, Card, EmptyState, Field, Input, Select } from '@aigovops/design-system';
import { Link } from 'react-router-dom';
import { firmApi, firmDirectoryApi, type FirmMember, type FirmRole } from '../../lib/firm-api';
import { fmtDate } from '../../lib/format';
import { Chip, GuardedButton, ROLE_LABEL, TabHeading, isLead, useAct, useDraft, useFirm, useFocusLater, useLoad, Loading } from './firm-kit';

const ADDABLE: FirmRole[] = ['manager', 'senior', 'staff'];

export function RosterTab() {
  const { slug, eng, o, lead, writable, lockId } = useFirm();
  const canEdit = lead && writable;
  const l = useLoad(() => firmApi.members(slug, eng), [slug, eng]);
  const staff = useLoad(() => (canEdit ? firmDirectoryApi.staff(o.firm.firm_id) : Promise.resolve({ staff: [] })), [o.firm.firm_id, canEdit]);
  const act = useAct(l.reload);
  const focus = useFocusLater();
  const [pick, setPick] = useDraft('roster-pick', '');
  const [role, setRole] = useDraft<FirmRole>('roster-role', 'staff');
  const [tried, setTried] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [reason, setReason] = useDraft('roster-reason', '');

  const active = (l.data?.members ?? []).filter((m) => !m.removed_at);
  const onRoster = new Set(active.map((m) => m.user_id));
  const candidates = (staff.data?.staff ?? []).filter((s) => s.accepted_at && !s.removed_at && !onRoster.has(s.user_id));
  const roles = o.me.firm_role === 'partner' ? (['partner', ...ADDABLE] as FirmRole[]) : ADDABLE;

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (!pick) return;
    const who = candidates.find((c) => c.user_id === pick)?.display_name ?? 'Member';
    if (await act.call('addMember', [slug, eng, { user_id: pick, firm_role: role }], `${who} added as ${ROLE_LABEL[role].toLowerCase()}`)) {
      setPick(''); setTried(false);
      focus('roster-h');
    }
  };
  const remove = async (m: FirmMember) => {
    if (reason.trim().length < 5) { setTried(true); return; }
    if (await act.call('removeMember', [slug, eng, m.member_id, reason.trim()], `${m.display_name} removed from the roster`)) {
      setRemoving(null); setReason(''); setTried(false);
      focus('roster-h');
    }
  };

  return (
    <div className="gp-firm-stack">
      <TabHeading id="roster-h">Roster</TabHeading>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      <Card>
        <Loading l={l}>{(d) => (
          <ul className="gp-firm-list" aria-labelledby="roster-h">
            {d.members.map((m) => (
              <li key={m.member_id} className="gp-firm-row" id={`member-${m.member_id}`}>
                <div className="gp-firm-row-head">
                  <h3 className="gp-firm-row-title">{m.display_name}</h3>
                  {m.removed_at ? <Chip>Removed</Chip> : <Chip>{ROLE_LABEL[m.firm_role]}</Chip>}
                </div>
                <p className="gp-firm-muted">{m.email} · added {fmtDate(m.added_at)}{m.removed_at ? ` · removed ${fmtDate(m.removed_at)}` : ''}</p>
                {canEdit && !m.removed_at && m.member_id !== o.me.member_id && (o.me.firm_role === 'partner' || m.firm_role !== 'partner') ? (
                  removing === m.member_id ? (
                    <div className="gp-firm-form">
                      <Field id={`rm-why-${m.member_id}`} label={`Why ${m.display_name} is leaving the engagement`} error={tried && reason.trim().length < 5 ? 'Give a reason (at least 5 characters).' : null}>
                        <Input value={reason} onChange={(e) => setReason(e.target.value)} />
                      </Field>
                      <div className="gp-firm-actions">
                        <GuardedButton busy={act.busy} onClick={() => void remove(m)}>Remove {m.display_name}</GuardedButton>
                        <GuardedButton variant="ghost" onClick={() => { setRemoving(null); setTried(false); focus(`member-${m.member_id}`); }}>Cancel</GuardedButton>
                      </div>
                    </div>
                  ) : (
                    <div className="gp-firm-actions">
                      <GuardedButton compact aria-label={`Remove ${m.display_name}`} onClick={() => { setRemoving(m.member_id); setTried(false); focus(`rm-why-${m.member_id}`); }}>Remove…</GuardedButton>
                    </div>
                  )
                ) : null}
              </li>
            ))}
          </ul>
        )}</Loading>
      </Card>
      {canEdit ? (
        <Card title="Add a member">
          {staff.data && candidates.length === 0 ? (
            <EmptyState title="Everyone on your staff list is on this engagement" action={<Link to="/audit-firm#staff">Invite staff by email</Link>} />
          ) : (
            <form className="gp-firm-form gp-firm-form--row" onSubmit={add} noValidate>
              <Field id="roster-pick" label="Person" error={tried && !pick ? 'Choose someone from your staff list.' : null}>
                <Select value={pick} onChange={(e) => setPick(e.target.value)}>
                  <option value="">Choose…</option>
                  {candidates.map((c) => <option key={c.user_id} value={c.user_id}>{c.display_name} · {ROLE_LABEL[c.firm_role]}</option>)}
                </Select>
              </Field>
              <Field id="roster-role" label="Role on this engagement">
                <Select value={role} onChange={(e) => setRole(e.target.value as FirmRole)}>
                  {roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                </Select>
              </Field>
              <div className="gp-firm-actions">
                <GuardedButton type="submit" variant="primary" busy={act.busy}>Add</GuardedButton>
                <Link to="/audit-firm#staff">Add from staff list</Link>
              </div>
            </form>
          )}
        </Card>
      ) : (
        <p className="gp-firm-muted" aria-describedby={lockId ?? undefined}>{isLead(o.me.firm_role) ? 'The roster is read-only now.' : 'A partner or manager changes the roster.'}</p>
      )}
    </div>
  );
}
